import React from 'react';
import { useQuery } from '@apollo/client';
import { getRecordFromResponse } from '@xbcb/shared-queries';
import { RecordType } from '@xbcb/shared-types';
import Milestones from 'components/Milestones';
import WorkOrderTaskSteps from 'components/WorkOrderTaskSteps';
import WorkOrderTaskInfoCard from 'components/WorkOrderTaskInfoCard';
import AutoExecutionDetails from 'components/WorkOrderTaskAutoExecutionDetails';
import { workOrderTaskQuery } from 'libs/sharedQueries';
import { DataCyTabPrefix, UiStage } from '@xbcb/ui-types';
import { getEnv, safeGetMessage } from '@xbcb/ui-utils';
import {
  evaluateWorkOrderTaskState,
  WorkOrderTaskEvaluatedState,
} from 'libs/evaluateWorkOrderTaskState';
import { useBundle } from '@amzn/react-arb-tools';
import { KatTabs, KatTab, KatProgress } from '@amzn/katal-components/react';
import '@amzn/katal-components/es/tabs';
import '@amzn/katal-components/es/progress';
import { WorkOrderTabsProps } from './component-antd'; // if swapping over entirely to katal, move imported type to this file

const WorkOrderTabsKatal = ({
  record,
  recordType,
  additionalInfoCards,
  loading,
  milestones,
  confirmations,
  pgaDispositions,
  inquiries,
  taskId,
  setTaskEvaluatedState,
}: WorkOrderTabsProps) => {
  const [bundle] = useBundle('components.WorkOrderTabs');
  const queryResponse = useQuery(workOrderTaskQuery, {
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });
  const { loading: queryLoading } = queryResponse;
  const { stage } = getEnv();
  const isDisabledForTesting = stage !== UiStage.PROD;
  const workOrderTask =
    getRecordFromResponse(queryResponse, 'get', RecordType.WORK_ORDER_TASK) ||
    {};

  if (taskId) {
    setTaskEvaluatedState(evaluateWorkOrderTaskState(workOrderTask));
  } else {
    setTaskEvaluatedState(WorkOrderTaskEvaluatedState.STARTED);
  }

  return (
    <KatTabs selected="2" data-cy={DataCyTabPrefix.WORK_ORDER_TABS}>
      <KatTab tabId="1" label={safeGetMessage(bundle, 'milestones')}>
        <Milestones
          loading={loading}
          milestones={milestones}
          confirmations={confirmations}
          pgaDispositions={pgaDispositions}
          inquiries={inquiries}
        />
      </KatTab>
      {taskId ? (
        <>
          <KatTab tabId="2" label={safeGetMessage(bundle, 'steps')}>
            {isDisabledForTesting && (
              <AutoExecutionDetails workOrderTask={workOrderTask} />
            )}
            <WorkOrderTaskSteps
              record={record}
              loading={loading || queryLoading}
              workOrderRecordType={recordType}
              workOrderTask={workOrderTask}
            />
          </KatTab>
          <KatTab tabId="3" label={safeGetMessage(bundle, 'task_info')}>
            <WorkOrderTaskInfoCard
              loading={loading || queryLoading}
              workOrderTask={workOrderTask}
            />
          </KatTab>
        </>
      ) : (
        additionalInfoCards && (
          <KatTab tabId="2" label={safeGetMessage(bundle, 'additional_info')}>
            {loading ? (
              <KatProgress indeterminate />
            ) : (
              additionalInfoCards.map((Card) => Card)
            )}
          </KatTab>
        )
      )}
    </KatTabs>
  );
};

export default WorkOrderTabsKatal;
